import {AS_SON_AS_POSIBLE, DELIVERY, SALON, SCHEDULED, TAKE_AWAY} from "@/modules/delivery/constants/OrderDelivery";
import {BAR} from "@/modules/delivery/constants/OrderOrigin";
import DeliveryModeService from "@/modules/delivery/core/services/DeliveryModeService";
import CartService from "@/modules/delivery/core/services/CartService";
import ContactService from "@/modules/delivery/core/services/ContactService";
import ScheduleService from "@/modules/delivery/core/services/ScheduleService";
import PaymentService from "@/modules/delivery/core/services/PaymentService";
import LocationService from "@/modules/delivery/core/services/LocationService";
import LocationHistoryService from "@/modules/delivery/core/services/LocationHistoryService";
import {Dayjs} from "@dracul/dayjs-frontend";

class OrderSystem {

    constructor() {

        this.settings = null

        this.branch = null

        this.order = null

        this.coupon = null

        this.couponInvalid = false

        this.categoryService = null

        this.locationHistory = []

        this.paymentMethods = []

        //Services
        this.deliveryModeService = new DeliveryModeService(this.order, this.settings, this.categoryService)
        this.cartService = new CartService(this.order, this.settings, this.categoryService)
        this.contactService = new ContactService(this.order, this.settings, this.categoryService)
        this.scheduleService = new ScheduleService(this.order, this.settings, this.categoryService)
        this.paymentService = new PaymentService(this.order, this.settings, this.categoryService)
        this.locationService = new LocationService(this.order, this.settings)
        this.locationHistoryService = new LocationHistoryService()

        //Calendars
        this.calendarTakeAway = null
        this.calendarDelivery = null

    }

    /**
     * @returns Order
     */
    get getOrder() {
        return this.order;
    }

    setOrder(order) {
        if (order.className != "Order") {
            throw new Error("Order must be an instance of Order");
        }
        this.order = order;
        this.deliveryModeService.setOrder(order);
        this.cartService.setOrder(order);
        this.contactService.setOrder(order);
        this.paymentService.setOrder(order);
        this.locationService.setOrder(order);
        this.scheduleService.setOrder(order);
    }

    setBranch(branch) {
        if (!branch || branch.className!= "Branch") {
            throw new Error("branch must be an instance of Branch");
        }
        if(!branch.id){
            throw new Error("branch id must be set")
        }
        this.branch = branch
        this.order.branch = branch.id
        this.deliveryModeService.setBranch(branch)
    }

    setLocationHistory(locationHistory) {
        this.locationHistory = locationHistory;
        this.locationHistoryService.setLocationHistory(locationHistory)
    }

    setPaymentMethods(paymentMethods) {
        // console.log("OrderSystem.setPaymentMethods", paymentMethods)
        this.paymentMethods = paymentMethods;
    }

    setSettings(orderSettings) {
        if (!orderSettings || orderSettings.className != "OrderSettings") {
            throw new Error("orderSettings must be an instance of OrderSettings");
        }
        this.settings = orderSettings
        this.scheduleService.setSettings(orderSettings)
        this.cartService.setSettings(orderSettings)
    }

    setCategoryService(categoryService) {
        if (!categoryService || categoryService.className != "CategoryService") {
            throw new Error("categoryService must be an instance of CategoryService");
        }
        this.categoryService = categoryService
        this.cartService.setCategoryService(categoryService)
    }

    get isSystemReady() {
        if (!this.settings || !this.getOrder || !this.categoryService || !this.getCalendarTakeAway || !this.getCalendarDelivery || !this.scheduleService || !this.branch) {
            return false
        }
        return true
    }


    /**
     * @returns CalendarModel
     */
    get getCalendarTakeAway() {
        return this.calendarTakeAway;
    }

    /**
     * @returns CalendarModel
     */
    get getCalendarDelivery() {
        return this.calendarDelivery;
    }

    setScheduleService(scheduleService) {
        if (scheduleService.className != "OrderScheduleService") {
            throw new Error("scheduleService must be an instance of OrderScheduleService");
        }
        this.scheduleService = scheduleService;
    }

    hasScheduleService() {
        return this.scheduleService != null
    }

    setCalendarTakeAway(calendarTakeAway) {
        if (calendarTakeAway.className != "CalendarModel") {
            throw new Error("calendarTakeAway must be an instance of CalendarModel");
        }
        this.calendarTakeAway = calendarTakeAway;
    }

    setCalendarDelivery(calendarDelivery) {
        if (calendarDelivery.className != "CalendarModel") {
            throw new Error("calendarDelivery must be an instance of CalendarModel");
        }
        this.calendarDelivery = calendarDelivery;
    }


    isCalendarActive(dateHour, extendedTime) {
        if (this.getOrder.getDeliveryMode === TAKE_AWAY) {
            return this.getCalendarTakeAway.isActive(dateHour, extendedTime)
        }
        if (this.getOrder.getDeliveryMode === DELIVERY) {
            return this.getCalendarDelivery.isActive(dateHour, extendedTime)
        }
        return false
    }

    isCalendarTakeAwayActive(extendedTime = 0) {
        let dateHour = Dayjs()
        return this.getCalendarTakeAway.isActive(dateHour, extendedTime)
    }

    isCalendarDeliveryActive(extendedTime = 0) {
        let dateHour = Dayjs()
        return this.getCalendarDelivery.isActive(dateHour, extendedTime)
    }

    get getScheduleOptions() {

        if (!this.scheduleService) {
            return []
        }

        if (this.getOrder.isTakeAway) {
            let useRange = false
            let useExtendedTime = this.getOrder.origin === BAR
            let targetDate = this.getOrder.delivery.date ? this.getOrder.delivery.date : null
            return this.scheduleService.getScheduleOptions(this.getCalendarTakeAway, useRange, useExtendedTime, targetDate)
        }
        if (this.getOrder.isDelivery) {
            let useRange = true
            let useExtendedTime = this.getOrder.origin === BAR
            let targetDate = this.getOrder.delivery.date ? this.getOrder.delivery.date : null
            return this.scheduleService.getScheduleOptions(this.getCalendarDelivery, useRange, useExtendedTime, targetDate)
        }
        return []
    }

    get getAsSoonAsPosible() {
        if (!this.scheduleService) {
            return null
        }

        if (this.getOrder.isTakeAway || this.getOrder.isSalon) {
            let useDeliveryTime = false
            return this.scheduleService.getAsSoonAsPosible(useDeliveryTime)
        }
        if (this.getOrder.isDelivery) {
            let useDeliveryTime = true
            return this.scheduleService.getAsSoonAsPosible(useDeliveryTime)
        }
        return null
    }

    // MODULES READY
    get isDeliveryReady() {
        return this.deliveryModeService.isReady
    }

    get isCartReady() {
        return this.cartService.isReady
    }

    get isContactReady() {
        return this.contactService.isReady
    }

    get isPaymentReady() {
        return this.paymentService.isReady
    }

    get isOrderReady() {
        //console.log(this.isDeliveryReady ,this.isCartReady ,this.isContactReady ,this.isPaymentReady)
        if (!this.isDeliveryReady || !this.isCartReady || !this.isContactReady || !this.isPaymentReady) {
            return false
        }
        return true
    }

    get isImmediateDeliveryEligible() {
        if (!this.order || !this.categoryService) {
            return false
        }

        if (this.order.origin !== BAR || !this.order.isTakeAway || !this.isOrderReady) {
            return false
        }

        const items = [
            ...this.order.items,
            ...this.order.promos.reduce((acc, orderPromo) => [...acc, ...orderPromo.items], [])
        ]

        if (items.length === 0) {
            return false
        }

        return items.every(item => {
            const category = this.categoryService.getCategoryById(item.category.id)
            return category && category.immediateDelivery === true
        })
    }

    get stepsReady() {


        if (!this.isDeliveryReady){
            return 0
        }

        if(!this.isCartReady){
            return 1
        }

        if(!this.isContactReady){
            return 2
        }

        if(!this.isPaymentReady){
            return 3
        }

        return 4

    }

    get getOrderForm() {
        return {
            origin: this.order.origin,
            delivery: this.order.delivery,
            contact: this.order.contact,
            location: this.order.location,
            payment: this.order.payment,
            branch: this.order.branch,
            discountAmount: this.order.discountAmount,
            discountRate: this.order.discountRate,
            deliveryFree: this.order.deliveryFree,
            employee: this.order.employee,
            couponCode: this.order.couponCode,
            items: this.order.items.map(item => ({
                product: item.product.id,
                category: item.category.id,
                quantity: item.quantity,
                order: item.order
            })),
            promos: this.order.promos.map(orderPromo => {
                let op = {}
                op._id = orderPromo._id
                op.promo = orderPromo.promo.id
                op.items = orderPromo.items.map(itemPromo => ({
                    product: itemPromo.product.id,
                    category: itemPromo.category.id,
                    quantity: itemPromo.quantity,
                    order: itemPromo.order
                }))
                return op
            })
        }

    }

    removeProductsByDeliveryMode(deliveryMode){
        this.cartService.removeOrderItemsByDeliveryMode(deliveryMode)
    }

    setOrderId(val){
        this.order.id = val
    }

    setOrderIdentifier(val){
        this.order.identifier = val
    }

    setOrderOrigin(origin){
        this.order.origin = origin
    }

    setOrderState(val){
        this.order.state = val
    }

    setOrderError(val){
        this.order.error = val
    }

    setDiscountRate(val){
        this.order.discountRate = val
    }

    setDiscountAmount(val){
        this.order.discountAmount = val
    }

    setDeliveryFree(val){
        this.order.deliveryFree = val
    }

    setEmployee(val){
        this.order.employee = val
    }


    //CART SERVICE
    addOrderItem(product, category, order = 999) {
        this.cartService.addOrderItem(product, category, order)
    }

    changeOrderItemQuantity(product, category, quantity, order){
        this.cartService.changeOrderItemQuantity(product, category, quantity, order)
    }

    removeOrderItem(product, category){
        this.cartService.removeOrderItem(product, category)
    }

    clearItems(){
        this.cartService.clearItems()
    }

    createOrderPromo(promo){
        return this.cartService.createOrderPromo(promo)
    }

    addOrderPromo(orderPromo){
        return this.cartService.addOrderPromo(orderPromo)
    }

    removeOrderPromo(orderPromo){
        return this.cartService.removeOrderPromo(orderPromo)
    }

    //DELIVERY MODE SERVICE

    setMode(deliveryMode){
        this.deliveryModeService.setMode(deliveryMode)
    }

    setTable(table){
        this.deliveryModeService.setTable(table)
    }

    setTimeMode(timeMode){
        this.deliveryModeService.setTimeMode(timeMode)
    }

    setTime(time){
        this.deliveryModeService.setTime(time)
    }

    setDate(date){
        this.deliveryModeService.setDate(date)
    }

    setOnBar(onBar){
        this.deliveryModeService.setOnBar(onBar)
    }

    setImmediateDelivery(immediateDelivery){
        this.order.delivery.immediateDelivery = immediateDelivery
    }

    //PAYMENT SERVICE


    findPaymentMethod(method){
        if (!this.paymentMethods) {
            return null
        }

        const paymentMethods = Object.values(this.paymentMethods)
            .filter(m => m && typeof m === 'object' && m.identifier)


        return paymentMethods.find(m => m.identifier === method)
    }

    setPaymentMethod(method){
        this.paymentService.setMethod(method)

        let paymentMethod = this.findPaymentMethod(method)


        if(!paymentMethod){
            throw new Error('Invalid payment method')
        }

        if(this.coupon && paymentMethod.discount > this.coupon.discountRate){
            this.order.discountRate = paymentMethod.discount
        }else if (paymentMethod.discount > 0){
            this.order.discountRate = paymentMethod.discount
        }else if(!this.coupon){
            this.order.discountRate = null
            this.order.discountAmount = null
        }

    }

    setCouponCode(couponCode){
        this.order.couponCode = couponCode
    }

    applyCoupon(coupon){
        if(coupon && coupon.code && coupon.discountType && coupon.discountValue){
            this.coupon = coupon
            this.couponInvalid = false
            this.order.couponCode = coupon.code
            if(coupon.discountType === 'PORCENTAJE'){
                this.order.discountRate = coupon.discountValue
            }else if(coupon.discountType === 'MONTO_FIJO'){
                this.order.discountAmount = coupon.discountValue
            }
        }else{
            this.order.discountRate = null
            this.order.discountAmount = null
            this.order.couponCode = null
            this.couponInvalid = true
        }

    }

    clearCoupon(){
        this.order.discountRate = null
        this.order.discountAmount = null
        this.order.couponCode = null
        this.couponInvalid = false
    }

    setReceiptFile(paymentReceiptFile){
        this.paymentService.setReceiptFile(paymentReceiptFile)
    }

    setPaymentConfirmed(paymentConfirmed){
        this.paymentService.setConfirmed(paymentConfirmed)
    }

    //GETTERS



    get isTakeAway() {
        return this.order.delivery.mode === TAKE_AWAY
    }

    get isDelivery() {
        return this.order.delivery.mode === DELIVERY
    }

    get isSalon() {
        return this.order.delivery.mode === SALON
    }

    get isAsSoonAsPossible() {
        return this.order.delivery.timeMode === AS_SON_AS_POSIBLE
    }

    get isScheduled() {
        return this.order.delivery.timeMode === SCHEDULED
    }

    //GETTERS - CART SERVICE

    get getDeliveryCost() {
        return this.cartService.getDeliveryCost
    }

    get getItemsAmount(){
        return this.cartService.getItemsAmount
    }

    get getTotalDiscount(){
        return this.cartService.getTotalDiscount
    }

    get getItemsQuantity(){
        return this.cartService.getItemsQuantity
    }

    get getPromoItemsQuantity(){
        return this.cartService.getPromoItemsQuantity
    }

    get getTotalQuantity(){
        return this.cartService.getTotalQuantity
    }

    get getPromosAndItemsAmount(){
        return this.cartService.getPromosAndItemsAmount
    }

    get getTotalAmount(){
        return this.cartService.getTotalAmount
    }

    get getTotalAmountWithDiscount(){
        return this.cartService.getTotalAmountWithDiscount
    }

    get hasDiscount(){
        return this.cartService.hasDiscount
    }


}


export default OrderSystem
